import { useTranslation } from "next-i18next";
import {
  CarPlusShieldEn,
  CarPlusShieldFr,
} from "@public/images/purchase/coverage";
import { ImageWithTranslation } from "@common/types/ImageWithTranslation";
import { useRouter } from "next/router";
import LocalImage from "@common/components/LocalImage";
import {
  ExtendedWarrantyCoverageDetails,
  ExtendedWarrantyRatesResponse,
  ProductCoverage,
} from "@modules/purchase/warranty/types/ExtendedWarrantyRates";
import WarrantyVehicleInfoCard from "./WarrantyVehicleInfoCard";
import WarrantyCoverageConfigurator from "./WarrantyCoverageConfigurator";

type Props = {
  extWarrantyRates: ExtendedWarrantyRatesResponse | undefined;
  rateDiscountValue?: number;
  onQuoteDifferentVehicle: (element: string) => void;
  onSelectCoverage: (
    coverage: ProductCoverage,
    selectedCoverageDetails: ExtendedWarrantyCoverageDetails
  ) => void;
};

const vehicleDefaultImage: ImageWithTranslation = {
  srcEn: CarPlusShieldEn,
  srcFr: CarPlusShieldFr,
  altKey: "purchase:alt_wty_vehicle_img",
};

const WarrantySelectCoverageHeadingWithConfiguration = ({
  extWarrantyRates,
  rateDiscountValue,
  onQuoteDifferentVehicle,
  onSelectCoverage,
}: Props) => {
  const { t } = useTranslation(["purchase", "common"]);
  const locale = useRouter().locale || "en";

  return (
    <div
      id="warranty-select-coverage-header-w-config"
      className="no-scrollbar relative overflow-visible bg-cover bg-center"
    >
      <div className="relative mx-auto flex px-4 lg:max-w-page flex-col-reverse lg:gap-5 items-center lg:items-start pb-6 pt-10 lg:flex-row-reverse lg:py-16">
        <div className="flex-1 md:px-28 lg:basis-[45%] lg:px-0">
          {extWarrantyRates ? (
            <WarrantyCoverageConfigurator
              ratesResponse={extWarrantyRates}
              rateDiscountValue={rateDiscountValue}
              onQuoteDifferentVehicle={onQuoteDifferentVehicle}
              onSelectCoverage={onSelectCoverage}
            />
          ) : (
            <div
              id="placeholder-wty-coverage-confg"
              className="hidden lg:flex flex-col gap-1 h-[48rem]"
            >
              <div className="h-full bg-gray-100 rounded-lg animate-pulse">
                {" "}
              </div>
            </div>
          )}
        </div>
        <div className="relative w-full flex-1 lg:basis-[55%]">
          <div className="flex flex-col items-center justify-center gap-4 lg:gap-6">
            <div className="flex flex-col text-white gap-6 px-4 md:px-5 lg:max-w-[30rem]">
              <p className="font-semibold uppercase">
                {t("purchase:coverage_made_simple")}
              </p>
              <h1 className="text-4xl">
                {t("purchase:this_vehicle_qualifies_for_warranty")}
              </h1>
              <p>{t("purchase:pay_monthly_for_peace_of_mind")}</p>
              <div className="flex lg:hidden items-center justify-center sm:px-0 md:px-20 lg:px-0">
                {extWarrantyRates?.vehicleDisplayDetails && (
                  <WarrantyVehicleInfoCard
                    vehicleDetails={extWarrantyRates.vehicleDisplayDetails}
                    className="text-white bg-gray-800"
                    ctaElements={
                      <div
                        className="text-xs md:text-sm underline mr-auto px-0 cursor-pointer text-white"
                        onClick={() =>
                          onQuoteDifferentVehicle("Quote-PlanConfigurator")
                        }
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            onQuoteDifferentVehicle("Quote-PlanConfigurator");
                          }
                        }}
                      >
                        {t("purchase:quote_different_vehicle")}
                      </div>
                    }
                  />
                )}
              </div>
            </div>
            <div className="relative inset-0 min-w-full">
              <div className="hidden lg:block relative overflow-auto h-80 md:h-96 lg:h-[35rem]">
                <LocalImage
                  className="z-[2] h-auto w-full rounded-lg object-contain"
                  src={
                    locale === "fr"
                      ? vehicleDefaultImage.srcFr
                      : vehicleDefaultImage.srcEn
                  }
                  alt={t(vehicleDefaultImage.altKey)}
                  fill
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarrantySelectCoverageHeadingWithConfiguration;
